// In production, set REACT_APP_API_URL (e.g. https://your-backend.onrender.com/api)
// in your hosting platform's env vars. Falls back to local dev.
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '');

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (this.token) {
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Request failed');
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(username, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  logout() {
    this.setToken(null);
  }

  // User endpoints
  async getUsers() {
    return this.request('/users');
  }

  async getUserProfile() {
    return this.request('/users/profile');
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async searchUsers(query) {
    return this.request(`/users/search?q=${encodeURIComponent(query)}`);
  }

  // Enhanced item endpoints with filtering
  async getItems(params = {}) {
    const queryString = new URLSearchParams();
    
    if (params.status && params.status !== 'all') {
      queryString.append('status', params.status);
    }
    if (params.type && params.type !== 'all') {
      queryString.append('type', params.type);
    }
    if (params.search) {
      queryString.append('search', params.search);
    }

    const endpoint = queryString.toString() ? `/items?${queryString}` : '/items';
    return this.request(endpoint);
  }

  async getItem(id) {
    return this.request(`/items/${id}`);
  }

  async createItem(itemData) {
    return this.request('/items', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  }

  async updateItem(id, itemData) {
    return this.request(`/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    });
  }

  async deleteItem(id) {
    return this.request(`/items/${id}`, {
      method: 'DELETE',
    });
  }

  async getAvailableItems() {
    return this.request('/items/available');
  }

  // File upload for Excel import
  async importItems(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/items/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Import failed');
    }

    return await response.json();
  }

  // Enhanced loan endpoints with filtering
  async getLoans(params = {}) {
    const queryString = new URLSearchParams();
    
    if (params.borrower && params.borrower !== 'all') {
      queryString.append('borrower', params.borrower);
    }
    if (params.status && params.status !== 'all') {
      queryString.append('status', params.status);
    }
    if (params.search) {
      queryString.append('search', params.search);
    }

    const endpoint = queryString.toString() ? `/loans?${queryString}` : '/loans';
    return this.request(endpoint);
  }

  async createLoanRequest(loanData) {
    return this.request('/loans', {
      method: 'POST',
      body: JSON.stringify(loanData),
    });
  }

  async approveLoan(id) {
    return this.request(`/loans/${id}/approve`, {
      method: 'POST',
    });
  }

  async denyLoan(id) {
    return this.request(`/loans/${id}/deny`, {
      method: 'POST',
    });
  }

  async returnItem(id, returnData = {}) {
    return this.request(`/loans/${id}/return`, {
      method: 'POST',
      body: JSON.stringify(returnData),
    });
  }

  // GSU Loaner Application endpoints
  async createLoanerApplication(applicationData) {
    return this.request('/loaner-applications', {
      method: 'POST',
      body: JSON.stringify(applicationData),
    });
  }

  async getLoanerApplications() {
    return this.request('/loaner-applications');
  }

  async approveLoanerApplication(id) {
    return this.request(`/loaner-applications/${id}/approve`, {
      method: 'POST',
    });
  }

  async denyLoanerApplication(id) {
    return this.request(`/loaner-applications/${id}/deny`, {
      method: 'POST',
    });
  }

  // Dashboard and reports
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  async getOverdueLoans() {
    return this.request('/reports/overdue');
  }

  // Categories and locations
  async getCategories() {
    return this.request('/categories');
  }

  async getLocations() {
    return this.request('/locations');
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // Utility methods for data processing
  static formatCurrency(amount) {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  static formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  }

  static calculateDaysOverdue(expectedReturn) {
    if (!expectedReturn) return 0;
    const today = new Date();
    const returnDate = new Date(expectedReturn);
    const diffTime = today - returnDate;
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }

  static getStatusColor(status) {
    const colors = {
      'available': 'bg-green-100 text-green-800',
      'loaned': 'bg-yellow-100 text-yellow-800',
      'maintenance': 'bg-blue-100 text-blue-800',
      'faulty': 'bg-red-100 text-red-800',
      'retired': 'bg-gray-100 text-gray-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'active': 'bg-green-100 text-green-800',
      'returned': 'bg-blue-100 text-blue-800',
      'denied': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  static getRoleColor(role) {
    const colors = {
      'admin': 'bg-red-100 text-red-800',
      'staff': 'bg-blue-100 text-blue-800',
      'borrower': 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  }

  static getUserTypeColor(userType) {
    const colors = {
      'student': 'bg-purple-100 text-purple-800',
      'GSU Team': 'bg-orange-100 text-orange-800',
      'staff': 'bg-blue-100 text-blue-800',
      'admin': 'bg-red-100 text-red-800'
    };
    return colors[userType] || 'bg-gray-100 text-gray-800';
  }

  static getConditionColor(condition) {
    const colors = {
      'excellent': 'bg-green-100 text-green-800',
      'good': 'bg-blue-100 text-blue-800',
      'fair': 'bg-yellow-100 text-yellow-800',
      'poor': 'bg-red-100 text-red-800'
    };
    return colors[condition] || 'bg-gray-100 text-gray-800';
  }

  // Data validation helpers
  static validateAssetId(assetId) {
    if (!assetId || assetId.trim().length === 0) {
      throw new Error('Asset ID is required');
    }
    if (assetId.length > 20) {
      throw new Error('Asset ID must be 20 characters or less');
    }
    return true;
  }

  static validateSerialNumber(serialNumber) {
    if (!serialNumber || serialNumber.trim().length === 0) {
      throw new Error('Serial Number is required');
    }
    return true;
  }

  static validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      throw new Error('Valid email address is required');
    }
    return true;
  }

  static validateGSUEmail(email, userType) {
    const studentEmailRegex = /@student\.gsu\.edu$/;
    const gsuEmailRegex = /@gsu\.edu$/;
    
    if (userType === 'student' && !studentEmailRegex.test(email)) {
      throw new Error('Students must use @student.gsu.edu email addresses');
    }
    
    if (userType === 'GSU Team' && !gsuEmailRegex.test(email)) {
      throw new Error('GSU Team members must use @gsu.edu email addresses');
    }
    
    return true;
  }

  static validatePantherID(pantherID) {
    const pantherRegex = /^\d{9}$/;
    if (pantherID && !pantherRegex.test(pantherID)) {
      throw new Error('Panther ID must be 9 digits');
    }
    return true;
  }

  static validatePassword(password) {
    if (!password || password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }
    return true;
  }

  static validateLoanDates(expectedReturn) {
    const today = new Date();
    const returnDate = new Date(expectedReturn);
    
    if (returnDate <= today) {
      throw new Error('Expected return date must be in the future');
    }
    
    const maxDays = 365; // Maximum loan period of 1 year
    const diffTime = returnDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > maxDays) {
      throw new Error('Loan period cannot exceed 1 year');
    }
    
    return true;
  }

  // Export data helpers
  static exportToCSV(data, filename) {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        `"${String(value || '').replace(/"/g, '""')}"`
      ).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Search and filter helpers
  static filterItems(items, filters) {
    return items.filter(item => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const searchableFields = [
          item.asset_id, item.type, item.brand, item.model, 
          item.serial_number, item.location_name, item.rcb_sticker_number
        ];
        const matchesSearch = searchableFields.some(field => 
          field && field.toLowerCase().includes(searchTerm)
        );
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && filters.status !== 'all') {
        if (item.status !== filters.status) return false;
      }

      // Type filter
      if (filters.type && filters.type !== 'all') {
        if (item.type !== filters.type) return false;
      }

      // Condition filter
      if (filters.condition && filters.condition !== 'all') {
        if (item.condition !== filters.condition) return false;
      }

      // Location filter
      if (filters.location && filters.location !== 'all') {
        if (item.location_id !== parseInt(filters.location)) return false;
      }

      // Can leave building filter
      if (filters.canLeaveBuilding !== undefined) {
        if (item.can_leave_building !== filters.canLeaveBuilding) return false;
      }

      return true;
    });
  }

  static filterLoans(loans, filters) {
    return loans.filter(loan => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const searchableFields = [
          loan.asset_id, loan.borrower_name, loan.reason,
          loan.approved_by_name, loan.returned_by_name, loan.panther_id
        ];
        const matchesSearch = searchableFields.some(field => 
          field && field.toLowerCase().includes(searchTerm)
        );
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && filters.status !== 'all') {
        if (loan.status !== filters.status) return false;
      }

      // Borrower filter
      if (filters.borrower && filters.borrower !== 'all') {
        if (loan.borrower_id !== parseInt(filters.borrower)) return false;
      }

      // User type filter
      if (filters.userType && filters.userType !== 'all') {
        if (loan.user_type !== filters.userType) return false;
      }

      // Application type filter
      if (filters.applicationType && filters.applicationType !== 'all') {
        if (loan.application_type !== filters.applicationType) return false;
      }

      // Date range filter
      if (filters.dateFrom) {
        const loanDate = new Date(loan.request_date || loan.checkout_date);
        const fromDate = new Date(filters.dateFrom);
        if (loanDate < fromDate) return false;
      }

      if (filters.dateTo) {
        const loanDate = new Date(loan.request_date || loan.checkout_date);
        const toDate = new Date(filters.dateTo);
        if (loanDate > toDate) return false;
      }

      return true;
    });
  }

  // Sort helpers
  static sortItems(items, sortBy, sortOrder = 'asc') {
    return [...items].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle different data types
      if (sortBy.includes('date') || sortBy.includes('Date')) {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else if (sortBy.includes('price') || sortBy.includes('Price')) {
        aVal = parseFloat(aVal || 0);
        bVal = parseFloat(bVal || 0);
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  static sortLoans(loans, sortBy, sortOrder = 'asc') {
    return [...loans].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle different data types
      if (sortBy.includes('date') || sortBy.includes('Date')) {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Analytics helpers for GSU data
  static getGSUInventoryStats(items) {
    const stats = {
      total: items.length,
      byStatus: {},
      byType: {},
      byCondition: {},
      byLocation: {},
      cannotLeaveBuilding: 0,
      canLeaveBuilding: 0,
      withRCBStickers: 0,
      totalValue: 0,
      averageAge: 0
    };

    items.forEach(item => {
      // Count by status
      stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
      
      // Count by type
      stats.byType[item.type] = (stats.byType[item.type] || 0) + 1;
      
      // Count by condition
      stats.byCondition[item.condition] = (stats.byCondition[item.condition] || 0) + 1;
      
      // Count by location
      const location = item.location_name || 'Unknown';
      stats.byLocation[location] = (stats.byLocation[location] || 0) + 1;
      
      // Count building restrictions
      if (item.can_leave_building) {
        stats.canLeaveBuilding++;
      } else {
        stats.cannotLeaveBuilding++;
      }
      
      // Count RCB stickers
      if (item.rcb_sticker_number) {
        stats.withRCBStickers++;
      }
      
      // Calculate total value
      if (item.purchase_price) {
        stats.totalValue += parseFloat(item.purchase_price);
      }
      
      // Calculate average age
      if (item.purchase_date) {
        const purchaseDate = new Date(item.purchase_date);
        const today = new Date();
        const ageInDays = (today - purchaseDate) / (1000 * 60 * 60 * 24);
        stats.averageAge += ageInDays;
      }
    });

    if (items.length > 0) {
      stats.averageAge = Math.round(stats.averageAge / items.length);
    }

    return stats;
  }

  static getGSULoanStats(loans) {
    const stats = {
      total: loans.length,
      byStatus: {},
      byUserType: {},
      byApplicationType: {},
      studentLoans: 0,
      gsuTeamLoans: 0,
      staffCreatedLoans: 0,
      selfRequestedLoans: 0,
      averageLoanDuration: 0,
      overdueCount: 0,
      returnRate: 0
    };

    let totalDuration = 0;
    let completedLoans = 0;
    let returnedLoans = 0;

    loans.forEach(loan => {
      // Count by status
      stats.byStatus[loan.status] = (stats.byStatus[loan.status] || 0) + 1;
      
      // Count by user type
      if (loan.user_type) {
        stats.byUserType[loan.user_type] = (stats.byUserType[loan.user_type] || 0) + 1;
        
        if (loan.user_type === 'student') {
          stats.studentLoans++;
        } else if (loan.user_type === 'GSU Team') {
          stats.gsuTeamLoans++;
        }
      }
      
      // Count by application type
      if (loan.application_type) {
        stats.byApplicationType[loan.application_type] = (stats.byApplicationType[loan.application_type] || 0) + 1;
        
        if (loan.application_type === 'staff_created') {
          stats.staffCreatedLoans++;
        } else {
          stats.selfRequestedLoans++;
        }
      }
      
      // Calculate overdue
      if (loan.status === 'active' && loan.expected_return) {
        const today = new Date();
        const expectedReturn = new Date(loan.expected_return);
        if (today > expectedReturn) {
          stats.overdueCount++;
        }
      }
      
      // Calculate average duration
      if (loan.checkout_date && loan.actual_return) {
        const checkoutDate = new Date(loan.checkout_date);
        const returnDate = new Date(loan.actual_return);
        const duration = (returnDate - checkoutDate) / (1000 * 60 * 60 * 24);
        totalDuration += duration;
        completedLoans++;
      }
      
      // Count returned loans
      if (loan.status === 'returned') {
        returnedLoans++;
      }
    });

    if (completedLoans > 0) {
      stats.averageLoanDuration = Math.round(totalDuration / completedLoans);
    }

    if (loans.length > 0) {
      stats.returnRate = Math.round((returnedLoans / loans.length) * 100);
    }

    return stats;
  }
}

const apiService = new ApiService();
export default apiService;